import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import {
  MutationEnablePluginArgs,
  MutationStartBoardExportArgs,
  MutationUpdateBoardOrgArgs,
  MutationUpdateBoardVisibilityArgs,
  MutationUpdateBoardCardCoversPrefArgs,
  MutationUpdateCalendarKeyArgs,
  MutationUpdateCalendarFeedEnabledPrefArgs,
  MutationRemoveMemberFromBoardArgs,
  MutationReopenBoardArgs,
  MutationDeleteBoardArgs,
  MutationUpdateEnabledPluginBoardButtonShortcutsArgs,
  MutationUpdatePluginHeaderShortcutsEnabledArgs,
  MutationCreateBoardInviteSecretArgs,
  MutationDisableBoardInviteSecretArgs,
  MutationAddMemberToBoardArgs,
  QueryBoardsSearchArgs,
} from '../generated';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { token } from '@trello/session-cookie';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { trelloFetch, fetch } from '@trello/fetch';
import { getNetworkClient } from '../getNetworkClient';
import { ResolverContext } from '../types';
import { Analytics } from '@trello/atlassian-analytics';
import Cookies from 'js-cookie';

export const enablePlugin = async (
  obj: object,
  args: MutationEnablePluginArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}/boardPlugins`);
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': args.traceId,
    },
    body: JSON.stringify({
      idPlugin: args.pluginId,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, info);
};

export const updateBoardOrg = async (
  obj: object,
  args: MutationUpdateBoardOrgArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };

  if (args.traceId) {
    headers['X-Trello-TraceId'] = args.traceId;
  }

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      idOrganization: args.orgId,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const updateBoardVisibility = async (
  obj: object,
  args: MutationUpdateBoardVisibilityArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      'prefs/permissionLevel': args.visibility,
      idOrganization: args.orgId,
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const updateBoardCardCoversPref = async (
  obj: object,
  args: MutationUpdateBoardCardCoversPrefArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/cardCovers': args.cardCovers,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const boardExportResolver = async (
  board: {
    id: string; // idBoard
  },
  args: {
    id: string; // idExport
  },
  context: ResolverContext,
  info: QueryInfo,
) => {
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${board.id}/exports/${args.id}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.export',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const startBoardExport = async (
  obj: object,
  args: MutationStartBoardExportArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.id}/exports`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        attachments: true,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const statsResolver = async (
  obj: object,
  args: { id: string },
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await trelloFetch(
    networkClient.getUrl(`/1/boards/${args.id}/stats`),
    { credentials: 'same-origin' },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.stats',
        operationName: context.operationName,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    } else {
      const error = await response.text();
      console.error(error);
      throw new Error(error);
    }
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info, 'Board');
};

export const boardDashboardViewTileResolver = async (
  board: {
    id: string; // idBoard
  },
  args: {
    id: string; // idTile
  },
  context: ResolverContext,
  info: QueryInfo,
) => {
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${board.id}/dashboardViewTiles/${args.id}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.DashboardViewTile',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const updateCalendarKey = async (
  obj: object,
  args: MutationUpdateCalendarKeyArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}/calendarKey/generate`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, info);
};

export const updateCalendarFeedEnabledPref = async (
  obj: object,
  args: MutationUpdateCalendarFeedEnabledPrefArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/calendarFeedEnabled': args.calendarFeedEnabled,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const removeMemberFromBoard = async (
  obj: object,
  { idBoard, idMember }: MutationRemoveMemberFromBoardArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  try {
    await fetch(`/1/Board/${idBoard}/members/${idMember}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    });

    return prepareDataForApolloCache({ success: true }, info);
  } catch (err) {
    return prepareDataForApolloCache(
      {
        success: false,
        error: (err as Error).message,
      },
      info,
    );
  }
};

export const reopenBoard = async (
  obj: object,
  args: MutationReopenBoardArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      closed: false,
      token: context.token || token,
      keepBillableGuests: args.keepBillableGuests,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const closeBoard = async (
  obj: object,
  args: MutationReopenBoardArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      closed: true,
      token: context.token || token,
      keepBillableGuests: args.keepBillableGuests,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const deleteBoard = async (
  obj: object,
  args: MutationDeleteBoardArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  await response.json();

  return prepareDataForApolloCache({ success: true }, info);
};

export const updateEnabledPluginBoardButtonShortcuts = async (
  obj: object,
  args: MutationUpdateEnabledPluginBoardButtonShortcutsArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const url = `/1/boards/${args.boardId}/prefs/enabledPluginBoardButtons/?value=${args.pluginId}`;
  const method = 'PUT';

  const response = await fetch(url, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, info);
};

export const updatePluginHeaderShortcutsEnabled = async (
  obj: object,
  args: MutationUpdatePluginHeaderShortcutsEnabledArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const url = `/1/boards/${args.boardId}/prefs/isPluginHeaderShortcutsEnabled/?value=${args.defaultEnabled}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, info);
};

export const boardInviteSecretRequestResolver = async (
  _parent: unknown,
  args: MutationCreateBoardInviteSecretArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await trelloFetch(
    url,
    { credentials: 'same-origin' },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.invite',
        operationName: context.operationName,
      },
    },
  );

  if (!response.ok) {
    return prepareDataForApolloCache({}, info);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, info);
};

export const createBoardInviteSecret = async (
  obj: object,
  args: MutationCreateBoardInviteSecretArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, info);
};

export const disableBoardInviteSecret = async (
  obj: object,
  args: MutationDisableBoardInviteSecretArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  await response.json();

  return prepareDataForApolloCache({ success: true }, info);
};

export const addMemberToBoard = async (
  obj: object,
  {
    boardId,
    member,
    type = 'normal',
    allowBillableGuest = false,
    traceId = '',
  }: MutationAddMemberToBoardArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const { id: memberId, email: memberEmail } = member;
  if (!memberId && !memberEmail) {
    throw new Error();
  }

  const urlParams = new URLSearchParams({ type: type! });
  allowBillableGuest && urlParams.append('allowBillableGuest', 'true');
  memberEmail && urlParams.append('email', memberEmail);

  const trailingPath =
    (memberId ? `/${memberId}` : '') + `?${urlParams.toString()}`;

  const additionalHeaders: HeadersInit = {};
  if (traceId) {
    additionalHeaders['X-Trello-TraceId'] = traceId;
  }

  const response = await fetch(
    networkClient.getUrl(`/1/boards/${boardId}/members${trailingPath}`),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        ...additionalHeaders,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, info);
};

export const boardsSearchResolver = async (
  obj: object,
  args: QueryBoardsSearchArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/search`);

  const params = new URLSearchParams();
  params.set('board_fields', 'all');
  params.set('query', args.query);
  params.set('boards_limit', args.limit.toString());
  params.set('modelTypes', 'boards');
  params.set('partial', (!args.query.endsWith(' ')).toString());
  const dscCookie = Cookies.get('dsc');
  if (dscCookie) {
    params.set('dsc', dscCookie);
  }

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'boardsSearch',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }
  return prepareDataForApolloCache((await response.json()).boards, info);
};
